import { AlertCircle, ImagePlus, Loader2, X } from 'lucide-react';
import {
    type ClipboardEvent,
    type DragEvent,
    type ReactNode,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';

import { Button } from '@apps/web/components/ui/button';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { cn } from '@apps/web/lib/utils';

const MAX_IMAGE_COUNT = 10;
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const ACCEPTED_IMAGE_TYPES = new Set(['image/jpeg', 'image/png']);

type ImageUpload = {
    readonly localId: string;
    readonly name: string;
    readonly previewUrl: string;
    readonly sizeBytes: number;
    readonly attachmentId: string | null;
    readonly status: 'uploading' | 'ready' | 'failed';
};

type UploadResponse = {
    readonly id: string;
    readonly size_bytes: number;
};

export function messageAndImagesFitMms(
    body: string,
    imageBytes: number,
): boolean {
    return (
        new TextEncoder().encode(body).byteLength + imageBytes <=
        MAX_IMAGE_BYTES
    );
}

export function useMessageImageUploads(organizationId: string) {
    const [uploads, setUploads] = useState<ReadonlyArray<ImageUpload>>([]);
    const generationRef = useRef(0);
    const uploadsRef = useRef<ReadonlyArray<ImageUpload>>([]);

    const updateUploads = useCallback(
        (
            update: (
                current: ReadonlyArray<ImageUpload>,
            ) => ReadonlyArray<ImageUpload>,
        ) => {
            const next = update(uploadsRef.current);
            uploadsRef.current = next;
            setUploads(next);
        },
        [],
    );

    const clear = useCallback(() => {
        generationRef.current += 1;
        updateUploads((current) => {
            current.forEach((upload) => URL.revokeObjectURL(upload.previewUrl));
            return [];
        });
    }, [updateUploads]);

    useEffect(() => clear, [clear]);

    const removeUpload = useCallback(
        (localId: string) => {
            updateUploads((current) => {
                const removed = current.find(
                    (upload) => upload.localId === localId,
                );
                if (removed) {
                    URL.revokeObjectURL(removed.previewUrl);
                }
                return current.filter((upload) => upload.localId !== localId);
            });
        },
        [updateUploads],
    );

    const addFiles = useCallback(
        async (files: Iterable<File>) => {
            const candidates = Array.from(files).filter((file) => {
                if (!ACCEPTED_IMAGE_TYPES.has(file.type)) {
                    toast.error('Choose a JPEG or PNG image', {
                        persistNotification: false,
                    });
                    return false;
                }
                if (file.size > MAX_IMAGE_BYTES) {
                    toast.error('Images must be 5 MB or smaller', {
                        persistNotification: false,
                    });
                    return false;
                }
                return true;
            });
            const available = Math.max(
                0,
                MAX_IMAGE_COUNT - uploadsRef.current.length,
            );
            if (candidates.length > available) {
                toast.error('You can attach up to 10 images', {
                    persistNotification: false,
                });
            }
            const countBounded = candidates.slice(0, available);
            let remainingBytes =
                MAX_IMAGE_BYTES -
                uploadsRef.current.reduce(
                    (total, upload) => total + upload.sizeBytes,
                    0,
                );
            const accepted = countBounded.filter((file) => {
                if (file.size > remainingBytes) {
                    return false;
                }
                remainingBytes -= file.size;
                return true;
            });
            if (accepted.length < countBounded.length) {
                toast.error(
                    'Attached images must be 5 MB or smaller in total',
                    {
                        persistNotification: false,
                    },
                );
            }
            if (!accepted.length) {
                return;
            }

            const generation = generationRef.current;
            const pending = accepted.map((file) => ({
                file,
                upload: {
                    localId: crypto.randomUUID(),
                    name: file.name || 'Image',
                    previewUrl: URL.createObjectURL(file),
                    sizeBytes: file.size,
                    attachmentId: null,
                    status: 'uploading' as const,
                },
            }));
            updateUploads((current) => [
                ...current,
                ...pending.map(({ upload }) => upload),
            ]);

            await Promise.all(
                pending.map(async ({ file, upload }) => {
                    try {
                        const formData = new FormData();
                        formData.append('file', file, file.name);
                        const response = await getApiClient().raw({
                            method: 'POST',
                            url: '/organizations/{organization_id}/conversations/attachments',
                            pathParams: {
                                organization_id: organizationId,
                            },
                            body: formData,
                        });
                        const result =
                            (await response.json()) as UploadResponse;
                        if (generation !== generationRef.current) {
                            return;
                        }
                        updateUploads((current) =>
                            current.map((currentUpload) =>
                                currentUpload.localId === upload.localId
                                    ? {
                                          ...currentUpload,
                                          attachmentId: result.id,
                                          // Use the server's normalized size;
                                          // re-encoding can make a PNG larger
                                          // than the raw browser file.
                                          sizeBytes: result.size_bytes,
                                          status: 'ready',
                                      }
                                    : currentUpload,
                            ),
                        );
                    } catch (error) {
                        console.error('Failed to upload image', error);
                        if (generation !== generationRef.current) {
                            return;
                        }
                        toast.error('Image upload failed. Try again.', {
                            persistNotification: false,
                        });
                        updateUploads((current) =>
                            current.map((currentUpload) =>
                                currentUpload.localId === upload.localId
                                    ? {
                                          ...currentUpload,
                                          status: 'failed',
                                      }
                                    : currentUpload,
                            ),
                        );
                    }
                }),
            );
        },
        [organizationId, updateUploads],
    );

    const handlePaste = useCallback(
        (event: ClipboardEvent<HTMLElement>) => {
            const imageFiles = Array.from(event.clipboardData.files).filter(
                (file) => file.type.startsWith('image/'),
            );
            if (!imageFiles.length) {
                return;
            }
            event.preventDefault();
            void addFiles(imageFiles);
        },
        [addFiles],
    );

    return {
        uploads,
        attachmentIds: uploads.flatMap((upload) =>
            upload.status === 'ready' && upload.attachmentId
                ? [upload.attachmentId]
                : [],
        ),
        isUploading: uploads.some((upload) => upload.status === 'uploading'),
        hasFailedUploads: uploads.some((upload) => upload.status === 'failed'),
        totalSizeBytes: uploads.reduce(
            (total, upload) => total + upload.sizeBytes,
            0,
        ),
        addFiles,
        clear,
        handlePaste,
        removeUpload,
    };
}

type MessageImageUploadControlsProps = {
    readonly uploads: ReadonlyArray<ImageUpload>;
    readonly disabled: boolean;
    readonly onAddFiles: (files: Iterable<File>) => void | Promise<void>;
    readonly onRemove: (localId: string) => void;
    readonly children?: ReactNode;
};

export function MessageImageUploadControls({
    uploads,
    disabled,
    onAddFiles,
    onRemove,
    children,
}: MessageImageUploadControlsProps) {
    const inputRef = useRef<HTMLInputElement>(null);
    const [isDragging, setIsDragging] = useState(false);

    const handleDrop = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragging(false);
        if (!disabled) {
            void onAddFiles(event.dataTransfer.files);
        }
    };

    return (
        <div
            className={cn(
                'space-y-2 rounded-md transition-colors',
                isDragging && 'bg-accent/60 ring-primary ring-2',
            )}
            onDragEnter={(event) => {
                event.preventDefault();
                if (!disabled) {
                    setIsDragging(true);
                }
            }}
            onDragLeave={(event) => {
                if (
                    !event.currentTarget.contains(event.relatedTarget as Node)
                ) {
                    setIsDragging(false);
                }
            }}
            onDragOver={(event) => event.preventDefault()}
            onDrop={handleDrop}
        >
            {uploads.length ? (
                <div
                    className="flex flex-wrap gap-2"
                    aria-label="Attached images"
                >
                    {uploads.map((upload) => (
                        <div
                            className="bg-muted relative size-20 overflow-hidden rounded-md border"
                            key={upload.localId}
                        >
                            <img
                                alt={upload.name}
                                className="size-full object-cover"
                                src={upload.previewUrl}
                            />
                            <div
                                className={cn(
                                    'absolute inset-0 flex items-center justify-center bg-black/35',
                                    upload.status === 'ready' && 'hidden',
                                )}
                            >
                                {upload.status === 'uploading' ? (
                                    <Loader2 className="size-5 animate-spin text-white" />
                                ) : (
                                    <AlertCircle className="size-5 text-red-300" />
                                )}
                            </div>
                            <button
                                aria-label={`Remove ${upload.name}`}
                                className="absolute top-1 right-1 rounded-full bg-black/65 p-0.5 text-white"
                                disabled={disabled}
                                onClick={() => onRemove(upload.localId)}
                                type="button"
                            >
                                <X className="size-3.5" />
                            </button>
                        </div>
                    ))}
                </div>
            ) : null}
            <div className="flex items-center gap-2">
                <input
                    accept="image/jpeg,image/png"
                    className="sr-only"
                    disabled={disabled}
                    multiple={true}
                    onChange={(event) => {
                        if (event.target.files) {
                            void onAddFiles(event.target.files);
                        }
                        event.target.value = '';
                    }}
                    ref={inputRef}
                    type="file"
                />
                <Button
                    aria-label="Attach images"
                    className="size-8"
                    disabled={disabled || uploads.length >= MAX_IMAGE_COUNT}
                    onClick={() => inputRef.current?.click()}
                    size="icon"
                    type="button"
                    variant="ghost"
                >
                    <ImagePlus className="size-4" />
                </Button>
                {children}
            </div>
        </div>
    );
}
